import { formatMoney } from '@theme/utilities';
import { QuantityBreaksUpdatedEvent, ThemeEvents } from '@theme/events';

class QuantityBreaks extends HTMLElement {
  constructor() {
    super();
    this.default_quantity_selected = null;
    this.quantity_break_items = null;
  }

  connectedCallback() {
    this.default_quantity_selected = this.dataset.quantitySelectedByDefault || 1;
    this.quantity_break_items = this.querySelectorAll('[data-ref="quantity-break-item"]');

    this.updateDefaultQuantitySelected();

    document.addEventListener('DOMContentLoaded', () => {
      if (this.getSelectedQuantity().dataset.quantity != 1) {
        this.dispatchEvent(new QuantityBreaksUpdatedEvent());
      }
      this._initQuantitySelectorListener();
    });

    this.quantity_break_items.forEach((quantity_break) => {
      quantity_break.addEventListener('click', () => {
        this.selectQuantity(quantity_break);
      });
    });

    document.addEventListener(ThemeEvents.variantUpdated, this.onVariantUpdated.bind(this));
    
    // Initialisation du listener pour le sélecteur de quantité
    this._initQuantitySelectorListener();
  }

  // Écoute les changements manuels du sélecteur (+/- ou clavier)
  _initQuantitySelectorListener() {
    const globalQuantityInput = document.querySelector('quantity-selector input[name="quantity"], .product-form__input[name="quantity"]');
    if (globalQuantityInput) {
      ['change', 'input'].forEach(evt => {
        globalQuantityInput.addEventListener(evt, () => {
          // On déclenche la mise à jour globale (ce qui recalculera getPrices)
          this.dispatchEvent(new QuantityBreaksUpdatedEvent());
        });
      });
    }
  }

  updateDefaultQuantitySelected() {
    this.resetSelectedQuantity();
    if (this.quantity_break_items[this.default_quantity_selected - 1]) {
        this.quantity_break_items[this.default_quantity_selected - 1].classList.add('quantity-break--selected');
    }
  }

  selectQuantity(quantity_break) {
    this.resetSelectedQuantity();
    quantity_break.classList.add('quantity-break--selected');

    const globalQuantityInput = document.querySelector('quantity-selector input[name="quantity"], .product-form__input[name="quantity"]');
    if (globalQuantityInput) {
      globalQuantityInput.value = quantity_break.dataset.quantity;
      globalQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.dispatchEvent(new QuantityBreaksUpdatedEvent());
  }

  resetSelectedQuantity() {
    this.quantity_break_items.forEach((quantity_break) => {
      quantity_break.classList.remove('quantity-break--selected');
    });
  }

  getSelectedQuantity() {
    const selected = this.querySelector('.quantity-break--selected');
    return selected || this.quantity_break_items[0];
  }

  // NOUVEAU : Calcul dynamique du prix total affiché
  getPrices() {
    const selectedBreak = this.getSelectedQuantity();
    const prices = selectedBreak.getPrices();
    
    const globalQuantityInput = document.querySelector('quantity-selector input[name="quantity"], .product-form__input[name="quantity"]');
    const globalQuantity = globalQuantityInput ? parseInt(globalQuantityInput.value) : 1;
    const breakQuantity = parseInt(selectedBreak.dataset.quantity) || 1;

    // Si l'utilisateur change la quantité manuellement, on calcule le prorata
    if (globalQuantity !== breakQuantity) {
      const unitPrice = parseFloat(prices.price) / breakQuantity;
      const unitComparePrice = parseFloat(prices.compareAtPrice) / breakQuantity;

      return {
        price: Math.round(unitPrice * globalQuantity).toString(),
        compareAtPrice: Math.round(unitComparePrice * globalQuantity).toString()
      };
    }

    return prices;
  }

  // Détermine ce qui est envoyé au panier
  getSelectedItems() {
    let items = [];
    const selectedBreak = this.getSelectedQuantity();
    
    const globalQuantityInput = document.querySelector('quantity-selector input[name="quantity"], .product-form__input[name="quantity"]');
    const globalQuantity = globalQuantityInput ? parseInt(globalQuantityInput.value) : 1;

    // On utilise TOUJOURS la valeur du sélecteur global si présent
    let finalQuantity = globalQuantityInput ? globalQuantity : parseInt(selectedBreak.dataset.quantity);

    if (selectedBreak.querySelector('[data-ref="quantity-break-variant-picker"]') != null) {
      selectedBreak
        .querySelectorAll('[data-ref="quantity-break-variant-picker"]')
        .forEach((variant_picker) => {
          items.push({
            id: variant_picker.value,
            quantity: globalQuantity > 1 ? globalQuantity : 1,
          });
        });
    } else {
      items.push({
        id: selectedBreak.dataset.variantId,
        quantity: finalQuantity,
      });
    }

    return items;
  }

  onVariantUpdated(event) {
    const currentQuantityBreaks = this;
    let newQuantityBreaks = currentQuantityBreaks;
    
    if (event && event.detail != null) {
      newQuantityBreaks = event.detail.data.html.querySelector('[data-ref="quantity-breaks"]');
    }

    if (!newQuantityBreaks || !currentQuantityBreaks) return;

    if (currentQuantityBreaks !== newQuantityBreaks) {
      currentQuantityBreaks.querySelectorAll('[data-ref="quantity-break-item"]').forEach((quantity_break, index) => {
        const newQuantityBreak = newQuantityBreaks.querySelectorAll('[data-ref="quantity-break-item"]')[index];
        if (newQuantityBreak) {
            quantity_break.render(newQuantityBreak);
        }
      });
      this.dispatchEvent(new QuantityBreaksUpdatedEvent());
    }
  }
}

customElements.define('quantity-breaks', QuantityBreaks);

class QuantityBreakItem extends HTMLElement {
  constructor() {
    super();
    this.variant_pickers = null;
    this.price = null;
  }

  connectedCallback() {
    this.variant_pickers = this.querySelectorAll('[data-ref="quantity-break-variant-picker"]');
    this.price = this.querySelector('[data-ref="quantity-break-price"]');

    this.variant_pickers.forEach((variant_picker) => {
      variant_picker.addEventListener('change', () => {
        this.#updateTotalPrice();
        this.dispatchEvent(new QuantityBreaksUpdatedEvent());
      });
    });
  }

  #updateTotalPrice() {
    let totalPrice = 0;
    let totalCompareAtPrice = 0;

    this.variant_pickers.forEach((variant_picker) => {
      const selectedOption = variant_picker.options[variant_picker.selectedIndex];
      totalPrice += parseInt(selectedOption.dataset.price);
      if (selectedOption.dataset.compareAtPrice) {
        totalCompareAtPrice += parseInt(selectedOption.dataset.compareAtPrice);
      } else {
        totalCompareAtPrice += parseInt(selectedOption.dataset.price);
      }
    });

    const discountType = this.dataset.discountType;
    const discountValue = this.dataset.discountValue;
    const discountPercentage = this.dataset.discountPercentage;

    let totalDiscountValue = discountValue;
    if (discountType === 'percentage') {
      totalDiscountValue = (totalPrice * discountPercentage) / 100;
    }

    totalPrice = totalPrice - totalDiscountValue;

    this.price.querySelector('[data-ref="price"]').innerHTML = formatMoney(totalPrice);
    this.dataset.totalPrice = totalPrice;

    if (totalCompareAtPrice > totalPrice) {
      this.price.querySelector('[data-ref="compare-at-price"]').innerHTML = formatMoney(totalCompareAtPrice);
      this.dataset.totalCompareAtPrice = totalCompareAtPrice;
    } else {
      this.price.querySelector('[data-ref="compare-at-price"]').innerHTML = '';
      this.dataset.totalCompareAtPrice = totalPrice;
    }
  }

  render(newQuantityBreak) {
    this.dataset.totalPrice = newQuantityBreak.dataset.totalPrice;
    this.dataset.totalCompareAtPrice = newQuantityBreak.dataset.totalCompareAtPrice;
    this.dataset.discountTotalValue = newQuantityBreak.dataset.discountTotalValue;

    this.price.querySelector('[data-ref="price"]').innerHTML = newQuantityBreak.querySelector('[data-ref="price"]').innerHTML;
    this.price.querySelector('[data-ref="compare-at-price"]').innerHTML = newQuantityBreak.querySelector('[data-ref="compare-at-price"]').innerHTML;

    const img = this.querySelector('[data-ref="quantity-break-image"]');
    const newImg = newQuantityBreak.querySelector('[data-ref="quantity-break-image"]');
    if (img && newImg) img.src = newImg.src;
  }

  getPrices() {
    return {
      price: this.dataset.totalPrice,
      compareAtPrice: this.dataset.totalCompareAtPrice,
    };
  }
}
customElements.define('quantity-break-item', QuantityBreakItem);